import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Overview from './pages/Overview'
import StudentDetail from './pages/StudentDetail'
import Meetings from './pages/Meetings'
import { UniversityList, APCourses, SATToefl, Transcripts, Activities, Applications, MyersBriggs } from './pages/SectionPages'
import AddStudent from './pages/AddStudent'
import {
  LayoutGrid, Calendar, UserPlus, Home, FileText,
  Activity, BookOpen, TrendingUp, Award, Brain, Users
} from 'lucide-react'

import './App.css'

function Sidebar({ activeFilter, setActiveFilter, studentCount }) {
  const location = useLocation()

  const navItems = [
    { to: '/', icon: LayoutGrid, label: 'Overview', exact: true },
    { to: '/meetings', icon: Calendar, label: 'Meetings' },
    { to: '/add-student', icon: UserPlus, label: 'Add student' },
  ]

  const sectionItems = [
    { to: '/university-list', icon: Home, label: 'University list' },
    { to: '/applications', icon: FileText, label: 'Applications' },
    { to: '/activities', icon: Activity, label: 'Activities' },
    { to: '/ap-courses', icon: BookOpen, label: 'AP courses' },
    { to: '/sat-toefl', icon: TrendingUp, label: 'SAT / TOEFL' },
    { to: '/transcripts', icon: Award, label: 'Transcripts' },
    { to: '/myers-briggs', icon: Brain, label: 'Myers-Briggs' },
  ]

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <span className="logo-text">eslite<em>apply</em></span>
        <span className="logo-sub">Admin dashboard</span>
      </div>

      {/* Active / Inactive toggle */}
      <div className="status-toggle">
        <button
          className={`toggle-btn ${activeFilter === 'Active' ? 'active' : ''}`}
          onClick={() => setActiveFilter('Active')}
        >
          <span className="toggle-dot active-dot" />
          Active
          <span className="toggle-count">{studentCount.active}</span>
        </button>
        <button
          className={`toggle-btn ${activeFilter === 'Inactive' ? 'active' : ''}`}
          onClick={() => setActiveFilter('Inactive')}
        >
          <span className="toggle-dot inactive-dot" />
          Inactive
          <span className="toggle-count">{studentCount.inactive}</span>
        </button>
      </div>

      <nav className="sidebar-nav">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `nav-item ${isActive ? 'nav-active' : ''}`}
          >
            <Icon size={15} />
            {label}
          </NavLink>
        ))}

        <div className="nav-section-label">Sections</div>

        {sectionItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `nav-item ${isActive ? 'nav-active' : ''}`}
          >
            <Icon size={15} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="footer-dot" />
        <span>All systems live</span>
      </div>
    </aside>
  )
}

function AppInner() {
  const [activeFilter, setActiveFilter] = useState('Active')
  const [studentCount, setStudentCount] = useState({ active: 0, inactive: 0 })

  useEffect(() => {
    const fetchCounts = async () => {
      const { data } = await supabase.from('students').select('status')
      if (data) {
        setStudentCount({
          active: data.filter(s => s.status === 'Active').length,
          inactive: data.filter(s => s.status === 'Inactive').length,
        })
      }
    }
    fetchCounts()
  }, [])

  const sharedProps = { activeFilter }

  return (
    <div className="app-shell">
      <Sidebar
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        studentCount={studentCount}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Overview {...sharedProps} />} />
          <Route path="/student/:id" element={<StudentDetail />} />
          <Route path="/meetings" element={<Meetings {...sharedProps} />} />
          <Route path="/add-student" element={<AddStudent />} />
          <Route path="/university-list" element={<UniversityList {...sharedProps} />} />
          <Route path="/applications" element={<Applications {...sharedProps} />} />
          <Route path="/activities" element={<Activities {...sharedProps} />} />
          <Route path="/ap-courses" element={<APCourses {...sharedProps} />} />
          <Route path="/sat-toefl" element={<SATToefl {...sharedProps} />} />
          <Route path="/transcripts" element={<Transcripts {...sharedProps} />} />
          <Route path="/myers-briggs" element={<MyersBriggs {...sharedProps} />} />
        </Routes>
      </main>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  )
}
